<!-- Copyright by the Spark Development Network; Licensed under the Rock Community License -->
<template>
    <TextBox v-model="internalValue" v-bind="fieldProps" :rules="rules" @update:modelValue="emit('update:baseModelValue', $event)" />
</template>

<script setup lang="ts">
    import { PropType, computed, ref } from "vue";
    import TextBox from "@Obsidian/Controls/textBox.obs";
    import { ListItemBag } from "@Obsidian/ViewModels/Utility/listItemBag";
    import { standardRockFormFieldProps, useStandardRockFormFieldProps, useVModelPassthrough } from "@Obsidian/Utility/component";
    import { normalizeRules } from "@Obsidian/ValidationRules";

    const props = defineProps({
        modelValue: {
            type: String,
            required: true
        },

        /** List of strings that are not allowed to appear in the value. */
        notAllowed: {
            type: Array as PropType<string[]>,
            default: []
        },

        ...standardRockFormFieldProps
    });

    const emit = defineEmits<{
        (e: "update:modelValue", value: ListItemBag): void;
        (e: "update:baseModelValue", value: string): void;
    }>();

    const internalValue = useVModelPassthrough(props, "modelValue", emit);

    const fieldProps = useStandardRockFormFieldProps(props);

    const disallowedStrings = computed(() => {
        const strings = ["&", " and ", "-and-", "_and_", " plus ", "+", "/"];
        strings.push(...props.notAllowed);
        return strings;
    });

    const rules = computed(() => {
        const rules = normalizeRules(props.rules);

        rules.push((value): string | boolean => {
            if (typeof value != "string") {
                return true;
            }

            const compareValue = value.toLocaleUpperCase();
            const found = disallowedStrings.value.filter(dont => compareValue.includes(dont.toLocaleUpperCase()));
            let foundSentenceList = "";

            if (found.length == 1) {
                foundSentenceList = `"${found[0]}"`;
            }
            else if (found.length == 2) {
                foundSentenceList = `"${found[0]}" or "${found[1]}"`;
            }
            else if (found.length > 2) {
                foundSentenceList = `"${found.slice(0, -1).join(`", "`)}", or "${found[found.length - 1]}"`;
            }

            if (found.length > 0) {
                return "cannot contain " + foundSentenceList;
            }

            return true;
        });

        return rules;
    });

</script>
